import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon, MagnifyingGlassIcon, PencilIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import {
  ShoppingBagIcon,
  ArchiveBoxIcon,
  UsersIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import DataTable from '../common/DataTable';
import StatCard from '../common/StatCard';
import EditPurchaseDetailsModal from './EditPurchaseDetailsModal';
import PayPurchaseDebtModal from './PayPurchaseDebtModal';
import { PDFDownloadButton } from './PurchaseReports';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const DatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
  <button
    onClick={onClick}
    ref={ref}
    type="button"
    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
  >
    <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    {value || 'Select date'}
  </button>
));

export default function PurchasesDashboard({ purchases: initialPurchases }) {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [totalPurchasesPeriod, setTotalPurchasesPeriod] = useState('month');
  const [totalOrdersPeriod, setTotalOrdersPeriod] = useState('month');
  const [filteredPurchases, setFilteredPurchases] = useState(initialPurchases);
  const [purchases, setPurchases] = useState(initialPurchases);
  const [periodTotalPurchases, setPeriodTotalPurchases] = useState(0);
  const [periodCompletedPurchases, setPeriodCompletedPurchases] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPayDebtModal, setShowPayDebtModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [businessDetails, setBusinessDetails] = useState(null);
  const [uniqueSuppliers, setUniqueSuppliers] = useState([]);

  // Calculate statistics
  const totalPurchases = purchases.reduce((sum, purchase) =>
    sum + (purchase.totalAmount || 0), 0);

  // Consider all purchases as "completed" since we don't have a status field
  const averagePurchaseValue = purchases.length > 0
    ? totalPurchases / purchases.length
    : 0;

  const pendingPayments = purchases.reduce((sum, purchase) => {
    const pending = (purchase.totalAmount || 0) - (purchase.amountPaid || 0);
    return sum + (pending > 0 ? pending : 0);
  }, 0);

  // Add useEffect for date range filtering
  useEffect(() => {
    if (!dateRange.startDate && !dateRange.endDate) {
      setFilteredPurchases(purchases);
      return;
    }

    const filtered = purchases.filter(purchase => {
      if (!purchase.createdAt) return false;
      const purchaseDate = new Date(purchase.createdAt);
      purchaseDate.setHours(0, 0, 0, 0);

      if (dateRange.startDate && dateRange.endDate) {
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return purchaseDate >= start && purchaseDate <= end;
      }

      if (dateRange.startDate) {
        const start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);
        return purchaseDate >= start;
      }

      if (dateRange.endDate) {
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        return purchaseDate <= end;
      }

      return true;
    });

    setFilteredPurchases(filtered);
  }, [dateRange, purchases]);

  // Add period calculation function
  const calculatePeriodStats = (period, statType) => {
    const setPeriod = statType === 'purchases' ? setTotalPurchasesPeriod : setTotalOrdersPeriod;
    setPeriod(period);

    const now = new Date();
    let startDate = new Date();

    switch (period) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      default:
        break;
    }

    // Remove the date filtering since we want to show all purchases
    const periodPurchases = purchases.filter(purchase => {
      if (!purchase.createdAt) return true; // Include purchases without dates
      const purchaseDate = new Date(purchase.createdAt);
      return purchaseDate >= startDate; // Remove the end date check
    });

    // Update stats based on period
    if (statType === 'purchases') {
      const totalAmount = periodPurchases.reduce((sum, purchase) =>
        sum + (purchase.totalAmount || 0), 0);
      setPeriodTotalPurchases(totalAmount);
    } else {
      setPeriodCompletedPurchases(periodPurchases);
    }
  };

  // Add this useEffect to initialize period stats
  useEffect(() => {
    if (purchases.length > 0) {
      calculatePeriodStats('month', 'purchases');
      calculatePeriodStats('month', 'orders');
    }
  }, [purchases]);

  // Add this useEffect after the other useEffects
  useEffect(() => {
    if (purchases.length > 0) {
      // Extract unique supplier names from purchases
      const suppliers = [...new Set(purchases
        .filter(purchase => purchase && purchase.supplierName)
        .map(purchase => purchase.supplierName))]
        .filter(Boolean)
        .sort();
      setUniqueSuppliers(suppliers);
    }
  }, [purchases]);

  // Modify stats_cards to include period toggles
  const stats_cards = [
    {
      title: 'Total Purchases',
      value: `₹${periodTotalPurchases.toLocaleString()}`,
      icon: CurrencyRupeeIcon,
      bgColor: 'bg-primary-500',
      borderColor: 'border-primary-500',
      hoverBgColor: 'hover:bg-primary-500',
      textColor: 'text-primary-500',
      periodType: 'purchases',
      currentPeriod: totalPurchasesPeriod,
      showPeriodToggle: true
    },
    {
      title: 'Total Orders',
      value: periodCompletedPurchases.length.toString(),
      icon: ShoppingBagIcon,
      bgColor: 'bg-green-500',
      borderColor: 'border-green-500',
      hoverBgColor: 'hover:bg-green-500',
      textColor: 'text-green-500',
      periodType: 'orders',
      currentPeriod: totalOrdersPeriod,
      showPeriodToggle: true
    },
    {
      title: 'Average Purchase Value',
      value: `₹${averagePurchaseValue.toFixed(2)}`,
      icon: ArchiveBoxIcon,
      iconBgColor: 'bg-yellow-500',
      borderColor: 'border-yellow-500',
      hoverBgColor: 'hover:bg-yellow-500',
      textColor: 'text-yellow-500',
      showPeriodToggle: false
    },
    {
      title: 'Pending Payments',
      value: `₹${pendingPayments.toLocaleString()}`,
      icon: UsersIcon,
      iconBgColor: 'bg-red-500',
      borderColor: 'border-red-500',
      hoverBgColor: 'hover:bg-red-500',
      textColor: 'text-red-500',
      showPeriodToggle: false
    }
  ];

  const columns = [
    {
      header: 'Date',
      accessorKey: 'createdAt',
      cell: (info) => {
        const date = new Date(info.getValue());
        return date.toLocaleDateString();
      },
    },
    {
      header: 'Supplier',
      accessorKey: 'supplierName',
    },
    {
      header: 'Items',
      accessorKey: 'items',
      cell: (info) => {
        const items = info.getValue();
        return (
          <div className="space-y-1">
            {items?.map((item, index) => (
              <div key={index} className="text-sm">
                {item.vegetableName || item.vegetable}: {item.quantity}kg @ ₹{item.pricePerKg}/kg
              </div>
            ))}
          </div>
        );
      },
    },
    {
      header: 'Total',
      accessorKey: 'totalAmount',
      cell: (info) => {
        const value = info.getValue();
        return value !== undefined ? `₹${value.toLocaleString()}` : '-';
      },
    },
    {
      header: 'Balance',
      accessorKey: 'remainingBalance',
      cell: (info) => {
        const value = info.getValue();
        return value !== undefined ? (
          <span className={value > 0 ? 'text-red-600 dark:text-red-400' : ''}>
            ₹{value.toLocaleString()}
          </span>
        ) : '-';
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => {
        const purchase = info.row.original;
        const hasRemainingBalance = (purchase.remainingBalance || 0) > 0;

        return (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedPurchase(purchase);
                setShowEditModal(true);
              }}
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
              text-primary-700 bg-primary-100 hover:bg-primary-200 
              dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </button>
            <button
              onClick={() => {
                setSelectedPurchase(purchase);
                setShowPayDebtModal(true);
              }}
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                text-primary-700 bg-primary-100 hover:bg-primary-200 
                dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
            >
              <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
              Pay Balance
            </button>
          </div>
        );
      },
    }
  ];

  // Add this function to handle purchase updates
  const handlePurchaseUpdated = (updatedPurchase) => {
    const updatedPurchases = purchases.map(p =>
      p.id === updatedPurchase.id ? updatedPurchase : p
    );
    setPurchases(updatedPurchases);
    setFilteredPurchases(updatedPurchases);
  };

  useEffect(() => {
    if (initialPurchases) {
      setPurchases(initialPurchases);
      setFilteredPurchases(initialPurchases);
    }
  }, [initialPurchases]);

  const renderReportsSection = () => (
    <div className="mb-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-lg font-medium mb-4 dark:text-white">Generate Reports</h2>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <select
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          >
            <option value="">All Suppliers</option>
            {uniqueSuppliers.map(supplier => (
              <option key={supplier} value={supplier}>{supplier}</option>
            ))}
          </select>
        </div>

        <DatePicker
          selectsRange={true}
          startDate={reportsDateRange.startDate}
          endDate={reportsDateRange.endDate}
          onChange={(update) => setReportsDateRange({ startDate: update[0], endDate: update[1] })}
          customInput={<DatePickerInput />}
          isClearable
          dateFormat="MM/dd/yyyy"
          maxDate={new Date()}
          minDate={new Date(2020, 0, 1)}
          showDisabledMonthNavigation
        />

        {filteredPurchases.length > 0 && (
          <PDFDownloadButton
            purchases={filteredPurchases}
            dateRange={reportsDateRange}
            businessDetails={businessDetails}
            supplierFilter={selectedSupplier}
          />
        )}
      </div>
    </div>
  );

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        const docRef = doc(db, 'settings', 'business');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBusinessDetails(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
      }
    };
    fetchBusinessDetails();
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats_cards.map((stat, index) => (
          <StatCard
            key={index}
            {...stat}
            onPeriodChange={calculatePeriodStats}
          />
        ))}
      </div>

      {renderReportsSection()}

      <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div className="relative rounded-md flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search purchases..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <DatePicker
                selectsRange={true}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(update) => {
                  setDateRange({ startDate: update[0], endDate: update[1] });
                }}
                customInput={<DatePickerInput />}
                dateFormat="dd/MM/yyyy"
                isClearable
                placeholderText="Select date range"
                maxDate={new Date()}
                showDisabledMonthNavigation
              />
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  onClick={() => setDateRange({ startDate: null, endDate: null })}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <DataTable
            data={filteredPurchases}
            columns={columns}
            sorting={sorting}
            setSorting={setSorting}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            showSearch={false}
          />
        </div>
      </div>

      {/* Edit Purchase Modal */}
      {showEditModal && (
        <EditPurchaseDetailsModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPurchase(null);
          }}
          purchase={selectedPurchase}
          onPurchaseUpdated={handlePurchaseUpdated}
        />
      )}

      {/* Pay Debt Modal */}
      {showPayDebtModal && (
        <PayPurchaseDebtModal
          isOpen={showPayDebtModal}
          onClose={() => {
            setShowPayDebtModal(false);
            setSelectedPurchase(null);
          }}
          purchase={selectedPurchase}
          onPurchaseUpdated={handlePurchaseUpdated}
        />
      )}
    </div>
  );
}
